import {
  Component,
  input,
  output,
  OnChanges,
  SimpleChanges,
  computed,
  ViewChild,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Popover } from 'primeng/popover';
import { Listbox } from 'primeng/listbox';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ListSelectionButtonConfig } from './list-selection-button.model';

export interface ListSelectionOption {
  key: string | number;
  value: any;
}

export interface ListSelectionGroup {
  label: string | number;
  items: ListSelectionOption[];
}

@Component({
  selector: 'cbre-list-selection-button',
  imports: [CommonModule, FormsModule, Popover, Listbox, MatButtonModule, MatIconModule, MatTooltipModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './list-selection-button.component.html',
  styleUrls: ['./list-selection-button.component.scss'],
})
export class ListSelectionButtonComponent implements OnChanges {
  // ─── Inputs ───────────────────────────────────────────────────────────────────

  listOptions = input<ListSelectionOption[] | ListSelectionGroup[]>([]);
  listBtnConfig = input<ListSelectionButtonConfig>({});

  // ─── Resolved config (listBtnConfig merged with hardcoded defaults) ───────────

  cfg = computed<Required<ListSelectionButtonConfig>>(() => ({
    btnLabel: 'More Filters',
    multiple: true,
    grouped: false,
    searchFilter: true,
    showBtnIcon: true,
    btnIcon: '',
    btnStyle: 'button',
    groupKey: 'label',
    optionLabel: 'value',
    optionValue: 'key',
    dataKey: 'key',
    maxHeight: '300px',
    clearBtnText: 'Clear All',
    maxSelection: 10,
    filterPlaceholder: 'Search options...',
    ...this.listBtnConfig(),
  }));

  /** Flat list of options to show in the listbox. */
  flatOptions = computed(() => {
    return this.cfg().grouped
      ? (this.listOptions() as ListSelectionGroup[])
      : (this.listOptions() as ListSelectionOption[]);
  });

  /**
   * Currently selected values — driven from outside so the listbox
   * stays in sync when filters are removed via cbre-filters chips.
   */
  selectedValues = input<any[]>([]);

  // ─── Outputs ──────────────────────────────────────────────────────────────────

  /** Emits the new selection array whenever the user checks/unchecks an option. */
  selectionChange = output<any[]>();

  // ─── Internal state ───────────────────────────────────────────────────────────

  /** Internal copy — ngModel binding for the p-listbox. */
  _selected: any[] = [];

  /** Disables only unselected options once the max of 10 is reached. */
  isOptionDisabled = computed(() => (option: any): boolean => {
    if (option?.disabled) return true;
    if ((this._selected?.length ?? 0) < 10) return false;
    const optionKey = option[this.cfg().optionValue];
    return !this._selected.includes(optionKey);
  });

  @ViewChild('op') private _popover!: Popover;

  // ─── Lifecycle ────────────────────────────────────────────────────────────────

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedValues']) {
      this._selected = [...(this.selectedValues() ?? [])];
    }
  }

  onSelectionChange(): void {
    const previous = new Set(this.selectedValues().map((k: any) => String(k)));
    const current = new Set(this._selected.map((k: any) => String(k)));

    // Find what was just unchecked
    const unchecked = [...previous].filter(k => !current.has(k));

    let expanded = [...this._selected];

    if (unchecked.length) {
      // Remove all siblings of unchecked group items
      expanded = this._collapseGroupSelections(expanded, unchecked);
    } else {
      // Add all siblings of newly checked group items
      expanded = this._expandGroupSelections(expanded);
    }

    this._selected = expanded;
    this.selectionChange.emit([...expanded]);
    setTimeout(() => this._popover?.align());
  }

  private _collapseGroupSelections(selected: any[], uncheckedKeys: string[]): any[] {
    const allOptions: ListSelectionOption[] = (this.listOptions() as any[]).flatMap((entry: any) =>
      entry.items ? entry.items : [entry]
    );

    const groupKeysToRemove = new Set<string>();
    allOptions.forEach(opt => {
      const o = opt as any;
      if (uncheckedKeys.includes(String(o.key)) && o.selectItemsInGroup && o.group_key) {
        groupKeysToRemove.add(o.group_key);
      }
    });

    if (!groupKeysToRemove.size) return selected;

    const keysToRemove = new Set<string>();
    allOptions.forEach(opt => {
      const o = opt as any;
      if (o.selectItemsInGroup && groupKeysToRemove.has(o.group_key)) {
        keysToRemove.add(String(o.key));
      }
    });

    return selected.filter(k => !keysToRemove.has(String(k)));
  }

  private _expandGroupSelections(selected: any[]): any[] {
    const selectedSet = new Set(selected.map(k => String(k)));
    const allOptions: ListSelectionOption[] = (this.listOptions() as any[]).flatMap((entry: any) =>
      entry.items ? entry.items : [entry]
    );

    const toAppend: any[] = [];
    allOptions.forEach(opt => {
      const o = opt as any;
      if (selectedSet.has(String(o.key)) && o.selectItemsInGroup && o.group_key) {
        allOptions
          .filter(s => (s as any).group_key === o.group_key && (s as any).selectItemsInGroup)
          .forEach(s => {
            const sk = String((s as any).key);
            if (!selectedSet.has(sk)) {
              selectedSet.add(sk);
              toAppend.push((s as any).key);
            }
          });
      }
    });

    return [...selected, ...toAppend];
  }

  clearSelection(_popover: any): void {
    const allOptions: ListSelectionOption[] = (this.listOptions() as any[]).flatMap((entry: any) =>
      entry.items ? entry.items : [entry]
    );
    const defaultKeys = allOptions.filter((o: any) => o.disabled).map((o: any) => o.key);
    this._selected = defaultKeys;
    this.selectionChange.emit(defaultKeys);
  }
}
